import { ref, onUnmounted } from "vue";

export function useDragAutoScroll({ threshold = 40, maxSpeed = 8 } = {}) {
  const scrollContainerRef = ref(null);
  let scrollAnimationId = null;
  let currentDragClientY = 0;

  const stopAutoScroll = () => {
    if (scrollAnimationId) {
      cancelAnimationFrame(scrollAnimationId);
      scrollAnimationId = null;
    }
  };

  const handleDragMove = (event) => {
    if (event.clientY) currentDragClientY = event.clientY;
  };

  const runAutoScroll = () => {
    scrollAnimationId = requestAnimationFrame(runAutoScroll);

    const container = scrollContainerRef.value;
    if (!container || !currentDragClientY) return;

    const containerRect = container.getBoundingClientRect();

    if (currentDragClientY < containerRect.top + threshold) {
      const distance = containerRect.top + threshold - currentDragClientY;
      container.scrollTop -= Math.min(maxSpeed, Math.max(2, (distance / threshold) * maxSpeed));
    } else if (currentDragClientY > containerRect.bottom - threshold) {
      const distance = currentDragClientY - (containerRect.bottom - threshold);
      container.scrollTop += Math.min(maxSpeed, Math.max(2, (distance / threshold) * maxSpeed));
    }
  };

  const handleSortStart = () => {
    currentDragClientY = 0;
    document.addEventListener("dragover", handleDragMove);
    document.addEventListener("drag", handleDragMove);
    scrollAnimationId = requestAnimationFrame(runAutoScroll);
  };

  const handleSortEnd = () => {
    stopAutoScroll();
    document.removeEventListener("dragover", handleDragMove);
    document.removeEventListener("drag", handleDragMove);
    currentDragClientY = 0;
  };

  onUnmounted(() => {
    stopAutoScroll();
    document.removeEventListener("dragover", handleDragMove);
    document.removeEventListener("drag", handleDragMove);
  });

  return { scrollContainerRef, handleSortStart, handleSortEnd };
}

export function createDragScrollHandler({
  scrollContainer,
  scrollThreshold = 50,
  scrollSpeed = 10,
  direction = "vertical",
  fixedLeftWidth = 0,
  fixedRightWidth = 0,
}) {
  let animationFrameId = null;

  const stop = () => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
  };

  const startScroll = (scrollDirection) => {
    stop();

    const scroll = () => {
      if (!scrollContainer) {
        stop();
        return;
      }

      let shouldContinue = false;

      switch (scrollDirection) {
        case "up":
          if (scrollContainer.scrollTop > 0) {
            scrollContainer.scrollTop -= scrollSpeed;
            shouldContinue = true;
          }
          break;
        case "down":
          if (
            scrollContainer.scrollTop <
            scrollContainer.scrollHeight - scrollContainer.clientHeight
          ) {
            scrollContainer.scrollTop += scrollSpeed;
            shouldContinue = true;
          }
          break;
        case "left":
          if (scrollContainer.scrollLeft > 0) {
            scrollContainer.scrollLeft -= scrollSpeed;
            shouldContinue = true;
          }
          break;
        case "right":
          if (
            scrollContainer.scrollLeft <
            scrollContainer.scrollWidth - scrollContainer.clientWidth
          ) {
            scrollContainer.scrollLeft += scrollSpeed;
            shouldContinue = true;
          }
          break;
      }

      if (shouldContinue) {
        animationFrameId = requestAnimationFrame(scroll);
      }
    };

    animationFrameId = requestAnimationFrame(scroll);
  };

  const update = (event) => {
    if (event.clientX === 0 && event.clientY === 0) {
      stop();
      return;
    }

    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    let shouldScroll = false;

    if (direction === "vertical" || direction === "both") {
      const distanceFromTop = event.clientY - containerRect.top;
      const distanceFromBottom = containerRect.bottom - event.clientY;

      if (distanceFromTop < scrollThreshold && distanceFromTop > 0) {
        startScroll("up");
        shouldScroll = true;
      } else if (
        distanceFromBottom < scrollThreshold &&
        distanceFromBottom > 0
      ) {
        startScroll("down");
        shouldScroll = true;
      }
    }

    if (!shouldScroll && (direction === "horizontal" || direction === "both")) {
      const distanceFromLeft =
        event.clientX - containerRect.left - fixedLeftWidth;
      const distanceFromRight =
        containerRect.right - event.clientX - fixedRightWidth;

      if (distanceFromLeft < scrollThreshold && distanceFromLeft > 0) {
        startScroll("left");
        shouldScroll = true;
      } else if (distanceFromRight < scrollThreshold && distanceFromRight > 0) {
        startScroll("right");
        shouldScroll = true;
      }
    }

    if (!shouldScroll) {
      stop();
    }
  };

  return { update, stop };
}
